import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { vlearnSurface, vlearnType } from "../vlearnUi";
import { formatVLearnFileSize } from "./DocumentLesson";

/**
 * V-Learning — a lesson whose content is a slide deck.
 *
 * A slides lesson holds up to two files, and they do different jobs. The PDF export is what
 * renders: it is the only one of the pair a browser can display, and it is shown in the same
 * inline viewer `PdfLesson` uses. The `.pptx` source is kept alongside it as a download so
 * the editable original is not lost to whoever needs to revise the course next.
 *
 * When only the source exists, the lesson says so plainly instead of showing an empty frame.
 * That is the designed behaviour, not a missing feature: no browser can render a PowerPoint
 * file without converting it first, and the only conversion services available would mean
 * sending internal course material to a third party. An author who wants the deck previewed
 * attaches its PDF export; until then, downloading it is the whole of what is possible.
 */

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

export default function SlidesLesson({ lesson }) {

    const files = (lesson && lesson.files) || []
    const pdfFile = files.find(file => file.kind === 'pdf') || null
    const sourceFile = files.find(file => file.kind === 'slides_source') || null

    const network = NetworkManager.getInstance()

    if (!pdfFile && !sourceFile) {
        return (
            <Box sx={{ ...vlearnSurface.panel }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_SLIDES_MISSING" />
                </Typography>
            </Box>
        )
    }

    const sourceButton = sourceFile ? (
        <Button
            size="small"
            variant={pdfFile ? 'outlined' : 'contained'}
            component="a"
            href={network.getVLearnFileUrl(sourceFile.id)}
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            sx={{
                ...focusRing, textTransform: 'none', fontSize: '0.82rem', flexShrink: 0,
                ...(pdfFile
                    ? { borderColor: 'rgba(0,0,0,0.18)', color: 'var(--text-primary, #111318)' }
                    : {
                        backgroundColor: 'var(--orange, rgb(255,144,21))',
                        '&:hover': { backgroundColor: '#e07f0d' }
                    })
            }}
        >
            <Langs str="VLEARN_SLIDES_DOWNLOAD_SOURCE" />
        </Button>
    ) : null

    if (!pdfFile) {
        const size = formatVLearnFileSize(sourceFile.size)
        return (
            <Box sx={{ ...vlearnSurface.panel, maxWidth: 640 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{
                        width: 46, height: 46, borderRadius: '12px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(217, 119, 6, 0.12)', color: '#D97706'
                    }} aria-hidden="true">
                        <SlideshowOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ ...vlearnType.sectionTitle }}>
                            <Langs str="VLEARN_SLIDES_NO_PREVIEW_TITLE" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.body, mt: 0.75 }}>
                            <Langs str="VLEARN_SLIDES_NO_PREVIEW_DESC" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.meta, mt: 1.25, wordBreak: 'break-word' }}>
                            {sourceFile.original_name}{size ? ` · ${size}` : ''}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mt: 2 }}>{sourceButton}</Box>
            </Box>
        )
    }

    return (
        <Box>
            <Box sx={{
                ...vlearnSurface.card,
                /*
                 * Shorter on a phone. `68vh` of a 640px viewport is 435px of iframe with the
                 * fallback text and the download button below the fold — and an iframed PDF
                 * is exactly where a phone is most likely to show nothing at all (iOS Safari
                 * renders the first page and refuses to scroll inside the frame; Android
                 * Chrome offers a download instead of a viewer). Keeping the escape hatch
                 * visible next to the frame matters more here than the extra 80px of preview.
                 */
                height: { xs: '52vh', sm: 'clamp(420px, 68vh, 900px)' },
                backgroundColor: 'var(--lightGrey, #ededed)',
                '& iframe': { display: 'block', width: '100%', height: '100%', border: 0 }
            }}>
                <iframe
                    src={network.getVLearnFileUrl(pdfFile.id)}
                    title={Localize('VLEARN_SLIDES_FRAME_TITLE').replace('%title%', lesson.title || '')}
                />
            </Box>

            <Box sx={{
                mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5,
                alignItems: 'center', justifyContent: 'space-between'
            }}>
                <Typography sx={{ ...vlearnType.meta, maxWidth: '52ch' }}>
                    <Langs str="VLEARN_PDF_FALLBACK" />
                </Typography>
                {/* Full width and stacked on a phone: when the frame above shows nothing —
                    which is the common case for an iframed PDF on iOS and Android — these two
                    are the only way to reach the deck, so they stop being a corner
                    afterthought. The 44px floor applies to both. */}
                <Box sx={{
                    display: 'flex', gap: 1,
                    width: { xs: '100%', sm: 'auto' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    '& .MuiButton-root': { minHeight: 44 }
                }}>
                    {sourceButton}
                    <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={network.getVLearnFileUrl(pdfFile.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            ...focusRing, textTransform: 'none', fontSize: '0.82rem', flexShrink: 0,
                            borderColor: 'rgba(0,0,0,0.18)', color: 'var(--text-primary, #111318)'
                        }}
                    >
                        <Langs str="VLEARN_SLIDES_DOWNLOAD_PDF" />
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
